/**
 * The setup journey as every surface must see it: filtered to the modules this
 * event actually has and ordered like the left sidebar (see visibleSections.ts
 * for the rule).
 *
 * ONE hook so the four surfaces can never disagree:
 *  - the panel decides whether the group exists (`open`),
 *  - the track renders the rows (`open`) and hands the sweep its scope
 *    (`moduleVisibleKeys` — DONE_MANUALLY is final, so the sweep must not touch
 *    a section the panel doesn't show),
 *  - the appbar pill draws the ring (`open` + `total`).
 */
import { useMemo } from "react";
import { useCapabilitiesOptional } from "@/providers/CapabilitiesProvider";
import { useEventLayoutEventQuery } from "@/lib/query/hooks/useEventLayoutEvent";
import { useEventRoleIsStale } from "@/lib/query/hooks/useEventLayoutRole";
import { useEventProgram } from "@/lib/query/hooks/useEventProgram";
import { isServerErr } from "@/lib/api/errors/ErrorHandler";
import type { Event } from "@/types/Event";
import { Plugin } from "@/types/enums/Plugin";
import type { SetupPlan, SetupPlanSection, SetupSectionKey } from "@/types/SetupPlan";
import { SECTION_NEEDS_BOOKABLE_ITEM } from "./sectionModules";
import { sectionsWithVisibleModule, visibleSetupSections } from "./visibleSections";

export interface SetupVisibility {
  /** Open rows, module-filtered, in sidebar order — what the panel renders. */
  open: SetupPlanSection[];
  /** Every section (any status) whose module is visible — the sweep's scope. */
  moduleVisibleKeys: ReadonlySet<SetupSectionKey>;
  /** Denominator of the pill's ring: `open` counted against this. */
  total: number;
}

const NO_KEYS: ReadonlySet<SetupSectionKey> = new Set<SetupSectionKey>();
const EMPTY: SetupVisibility = { open: [], moduleVisibleKeys: NO_KEYS, total: 0 };

export function useSetupVisibility(
  eventId: string | null,
  plan: SetupPlan | null | undefined,
): SetupVisibility {
  const { can } = useCapabilitiesOptional();
  // The shell's role query keeps the PREVIOUS event's role painted while a new
  // event loads. For the chrome that is a feature; here it would gate event B's
  // plan with event A's capabilities — the authz half of the rule. Read the
  // cache directly so there is no wiring step anyone can forget.
  const roleIsStale = useEventRoleIsStale(eventId);

  // Only read the event once there is something to filter — outside an event
  // (or before the plan lands) this hook must not fire a request of its own.
  // Inside an event the shell already holds this exact cache entry.
  const query = useEventLayoutEventQuery(plan?.sections?.length ? eventId : null);
  // Same reasoning for the plugin half: a placeholder here is event A's plugin
  // list, so treat it as "not loaded" and let the documented fail-open apply.
  const event =
    query.data && !query.isPlaceholderData && !isServerErr(query.data)
      ? (query.data as Event)
      : undefined;

  const plugins = event?.plugins;
  const sidebarOrder = event?.sidebarOrder;
  const sections = plan?.sections;

  // A bare Appointments (OFFERING) row needs a bookable programme item, not
  // just SLOTTING (one switch with PROGRAM now — see
  // SECTION_NEEDS_BOOKABLE_ITEM). Read the programme only for a plan that has
  // such a row on a SLOTTING event; same cache entry as the programme page.
  const needsProgramme =
    !!plugins?.includes(Plugin.SLOTTING) &&
    (sections ?? []).some(
      (s) => SECTION_NEEDS_BOOKABLE_ITEM.has(s.key) && !s.proposal?.trim(),
    );
  const programQ = useEventProgram(needsProgramme ? eventId : null);
  const hasBookableProgramItem = Array.isArray(programQ.data)
    ? programQ.data.some((item) => item.kind === "OFFERING")
    : undefined;

  // The FIRST event read still in flight (TanStack v5 `isPending`: no data yet,
  // including its retry phase and a disabled query; NOT placeholder data, NOT
  // an error). Show nothing until the plugins land: failing open here would
  // flash rows whose module is off — the create flow stores briefed sections
  // regardless of their plugin. A FAILED read, a server-error payload and the
  // keepPreviousData placeholder of an event switch still fail open (below).
  // Checked INSIDE the memo: an early return before useMemo would be a
  // conditional hook.
  const pending = query.isPending;

  return useMemo(() => {
    if (roleIsStale || pending) return EMPTY;
    const input = { plugins, can, sidebarOrder, hasBookableProgramItem };
    const open = visibleSetupSections(sections ?? [], input);
    const withModule = sectionsWithVisibleModule(sections ?? [], input);
    return {
      open,
      moduleVisibleKeys: new Set(withModule.map((s) => s.key)),
      // Count the denominator over the SAME set the rows come from: a section
      // the rule strands (WORKFLOWS without a reachable EMAILS row) is neither
      // open nor done, and counting it as done invents progress.
      total: withModule.filter((s) => !isStrandedOut(s, open)).length,
    };
  }, [sections, plugins, sidebarOrder, can, roleIsStale, pending, hasBookableProgramItem]);
}

/** Module-visible, still unresolved, yet not rendered = dropped by a rule. */
function isStrandedOut(section: SetupPlanSection, open: SetupPlanSection[]): boolean {
  if (section.status !== "PROPOSED" && section.status !== "BUILDING") return false;
  return !open.some((o) => o.key === section.key);
}

/** Convenience for the two surfaces that only need the rows. */
export function useVisibleSetupSections(
  eventId: string | null,
  plan: SetupPlan | null | undefined,
): SetupPlanSection[] {
  return useSetupVisibility(eventId, plan).open;
}
